<template>
  <div>
    <v-text-field
      :model-value="thumbnailResource ? thumbnailResource.name : modelValue"
      readonly
      clearable
      :label="label"
      :error-messages="errorMessages"
      hide-details="auto"
      @click:clear="clear"
    >
      <template v-slot:append>
        <v-tooltip text="Lookup resource for thumbnail" location="top">
          <template v-slot:activator="{ props: tooltipProps }">
            <v-btn icon variant="text" v-bind="tooltipProps" @click="choose">
              <v-icon>mdi-database-search</v-icon>
            </v-btn>
          </template>
        </v-tooltip>
      </template>
    </v-text-field>
    <div v-if="thumbnailResource" class="text-body-2 text-medium-emphasis d-flex align-center ml-4 mb-2">
      <span>Resource id: {{ thumbnailResource.uuid }}</span>
      <v-tooltip v-if="thumbnailResource.status !== ResourceStatus.PUBLISHED" text="This resource is not published" location="top">
        <template v-slot:activator="{ props: tooltipProps }">
          <v-icon v-bind="tooltipProps" color="deep-orange" size="small" class="ml-1">mdi-alert</v-icon>
        </template>
      </v-tooltip>
    </div>
    <resource-picker ref="resourcePicker" />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, inject, onMounted } from 'vue';
import type { AdminResourcesReadRequest, ResourceDto, ResourceId } from '@/venit/services/model/techpubs-cms';
import { ResourceStatus } from '@/venit/services/model/techpubs-cms';
import ResourceManagementService from '@/venit/admin/resource-management/resource-management.service';
import ResourcePicker from '@/venit/components/resource-picker/resource-picker-dialog.vue';

const props = withDefaults(defineProps<{
  modelValue: string | null | undefined;
  label: string;
  errorMessages?: string[];
}>(), {
  errorMessages: () => [],
});

const emit = defineEmits<{
  'update:modelValue': [value: string | null];
}>();

const resourcesManagementService = inject<() => ResourceManagementService>('resourceService')!;
const resourcePicker = ref<InstanceType<typeof ResourcePicker> | null>(null);

const thumbnailResource = ref<{ name: string; uuid: string; status: ResourceStatus } | null>(null);

const loadThumbnailResource = (uuid: string | null | undefined) => {
  if (!uuid) {
    thumbnailResource.value = null;
    return;
  }
  const request: AdminResourcesReadRequest = { resourceId: { uuid } };
  resourcesManagementService()
    .readResource(request)
    .then(response => {
      thumbnailResource.value = { name: response.data.resourceValue.name, uuid, status: response.data.resourceValue.status };
    });
};

const choose = async () => {
  const resourceId: ResourceId = { uuid: props.modelValue ?? null };
  const resource: ResourceDto | null = await resourcePicker.value?.openDefault(resourceId);
  if (resource) {
    thumbnailResource.value = { name: resource.name, uuid: resource.uuid, status: resource.status };
    emit('update:modelValue', resource.uuid);
  }
};

const clear = () => {
  thumbnailResource.value = null;
  emit('update:modelValue', null);
};

watch(() => props.modelValue, (uuid) => {
  if (uuid !== thumbnailResource.value?.uuid) {
    loadThumbnailResource(uuid);
  }
});

onMounted(() => {
  loadThumbnailResource(props.modelValue);
});
</script>
